import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";

function tokenHeaders() {
  const t = localStorage.getItem("aig_access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function InvoicesPage({ status }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tpl, setTpl] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [minA, setMinA] = useState("");
  const [maxA, setMaxA] = useState("");

  const load = async () => {
    const params = { status };
    if (q) params.q = q;
    if (tpl && tpl !== "all") params.template = tpl;
    if (minA) params.min_amount = minA;
    if (maxA) params.max_amount = maxA;
    const { data } = await api.get("/invoices", { params });
    setRows(data);
  };
  useEffect(() => { load(); api.get("/templates").then((r) => setTemplates(r.data)); /* eslint-disable-next-line */ }, [status]);

  const openPdf = async (id) => {
    const res = await fetch(`${API_BASE}/invoices/${id}/pdf`, { credentials: "include", headers: tokenHeaders() });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const approveOne = async (id) => {
    try { await api.post("/invoices/approve", { invoice_ids: [id] }); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="invoices-title">
          {status === "draft" ? "Draft Invoices" : "Approved Invoices"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {status === "draft"
            ? "Editable drafts pending reconciliation and approval. All drafts carry a DRAFT watermark."
            : "Finalised, immutable invoices. Cryptographic PDF hash recorded in audit."}
        </p>
      </div>

      <div className="card-panel p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-muted-foreground">Search</label>
          <Input placeholder="Invoice # or party" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Template</label>
          <Select value={tpl} onValueChange={setTpl}>
            <SelectTrigger className="w-40" data-testid="template-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              {templates.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Min amount</label>
          <Input type="number" value={minA} onChange={(e) => setMinA(e.target.value)} className="w-32" data-testid="min-amount" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Max amount</label>
          <Input type="number" value={maxA} onChange={(e) => setMaxA(e.target.value)} className="w-32" data-testid="max-amount" />
        </div>
        <Button onClick={load} data-testid="apply-filters">Apply</Button>
      </div>

      <div className="card-panel">
        <table className="w-full table-dense" data-testid="invoices-table">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left">Invoice #</th>
              <th className="text-left">Party</th>
              <th className="text-left">Template</th>
              <th className="text-right">Amount</th>
              <th className="text-left">Date</th>
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`invoice-row-${r.id}`}>
                <td className="font-mono text-xs">{r.invoice_number}</td>
                <td><Link to={`/parties/${r.party_id}`} className="hover:underline">{r.issuer_name || r.party_name || "—"}</Link></td>
                <td><span className="beige-tag px-2 py-0.5 rounded text-xs">{r.template_code}</span></td>
                <td className="num-cell">{formatInr(r.amount)}</td>
                <td className="text-xs">{r.invoice_date}</td>
                <td><StatusBadge status={r.status} /></td>
                <td className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openPdf(r.id)} data-testid={`open-pdf-${r.id}`}><Eye size={14} className="mr-1" />PDF</Button>
                  {r.status === "draft" && hasRole(user, "admin", "reviewer") && (
                    <Button size="sm" variant="ghost" onClick={() => approveOne(r.id)} data-testid={`quick-approve-${r.id}`}><CheckCircle size={14} className="mr-1" />Approve</Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No invoices found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
